// vendors
import React, { useCallback, useEffect } from 'react';
// import screenfull from "screenfull";
import { ButtonGroup, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import 'styled-components/macro';

// context
import { playbackRateOptions, useVideoPlayer } from '../VideoPlayer.context';

// utils
import { secondsToTimeShort } from '../../../utils/formatTime';

// styles
import {
  Container,
  Button,
  Timeline,
  TimeStamp,
  VolumeWrapper,
  VolumeSliderWrapper,
  VolumeSlider,
  Wrapper,
} from './Controls.styles';

const renderPlaybackRateOption = (rate, { handleClick, modifiers }) => {
  return (
    <MenuItem
      key={rate}
      text={rate}
      // label={rate}
      active={modifiers.active}
      onClick={handleClick}
    />
  );
};

const Controls = () => {
  const {
    // playerRef,
    isPlaying,
    toggleIsPlaying,
    setPlaybackRate,
    playbackRate,
    seekBackward,
    seekToPercentage,
    progress,
    duration,
    volume,
    setVolume,
    muted,
    setMuted,
    increasePlaybackRate,
    decreasePlaybackRate,
    seekForward,
  } = useVideoPlayer();

  const handleKeyDown = useCallback(
    (e) => {
      if (
        e.shiftKey &&
        e.code === 'Space' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();

        toggleIsPlaying();
      }

      if (
        e.shiftKey &&
        e.code === 'ArrowUp' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();

        increasePlaybackRate();
      }

      if (
        e.shiftKey &&
        e.code === 'ArrowDown' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();

        decreasePlaybackRate();
      }

      if (
        e.shiftKey &&
        e.code === 'ArrowLeft' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();

        seekBackward(5);
      }

      if (
        e.shiftKey &&
        e.code === 'ArrowRight' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        e.preventDefault();

        seekForward(5);
      }
    },
    [
      toggleIsPlaying,
      increasePlaybackRate,
      decreasePlaybackRate,
      seekForward,
      seekBackward,
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSeekForward = useCallback(() => seekForward(5), [seekForward]);

  const handleSeekBackward = useCallback(() => seekBackward(5), [seekBackward]);

  const handleSeekChange = useCallback(
    (value) => seekToPercentage(value / 100),
    [seekToPercentage]
  );

  const handleVolumeChange = useCallback(
    (value) => {
      setVolume(value / 100);
    },
    [setVolume]
  );

  const handleMuted = useCallback(() => {
    setMuted(!muted);
  }, [muted, setMuted]);

  // const handleFullscreen = useCallback(() => {
  //   screenfull.request(findDOMNode(playerRef.current));
  // }, [playerRef]);

  const labelRenderer = useCallback(
    (value) => {
      const currentTime = (duration / 100) * value;

      return secondsToTimeShort(currentTime, duration);
    },
    [duration]
  );

  const volumeIconStatus = useCallback(() => {
    if (volume === 0 || muted) return 'off';

    if (volume > 0.5) return 'up';

    return 'down';
  }, [volume, muted]);

  return (
    <Container>
      <Wrapper>
        <div
          css={`
            grid-column: 1;
            grid-row: 1;
            margin: 0 1rem;
          `}
        >
          <Timeline
            value={progress.played * 100}
            max={100}
            labelStepSize={100}
            stepSize={0.1}
            labelRenderer={labelRenderer}
            onChange={handleSeekChange}
          />
        </div>

        <div
          css={`
            grid-column: 1;
            grid-row: 2;

            display: flex;
            justify-content: space-between;
            align-items: center;

            margin: 0 8px;
          `}
        >
          <ButtonGroup
            css={`
              margin-left: 5px;

              > * + * {
                margin-left: 10px;
              }
            `}
          >
            <Button
              icon={isPlaying ? 'pause' : 'play'}
              minimal
              onClick={toggleIsPlaying}
            />
            <Button minimal icon="step-backward" onClick={handleSeekBackward} />
            <Button
              minimal
              rightIcon="step-forward"
              onClick={handleSeekForward}
            />
            <VolumeWrapper>
              <Button
                minimal
                icon={`volume-${volumeIconStatus()}`}
                onClick={handleMuted}
              />

              <VolumeSliderWrapper id="volume-wrapper">
                <VolumeSlider
                  value={volume * 100}
                  max={100}
                  stepSize={1}
                  onChange={handleVolumeChange}
                />
              </VolumeSliderWrapper>
            </VolumeWrapper>

            <TimeStamp>
              <span>{`${labelRenderer(progress.played * 100)} / ${labelRenderer(
                100
              )}`}</span>
            </TimeStamp>
          </ButtonGroup>

          <ButtonGroup
            css={`
              margin-right: 5px;
            `}
          >
            <Select
              activeItem={playbackRate}
              items={playbackRateOptions}
              itemRenderer={renderPlaybackRateOption}
              filterable={false}
              itemsEqual={(a, b) => a === b}
              onItemSelect={setPlaybackRate}
            >
              <Button
                minimal
                icon="dashboard"
                text={`Speed (${playbackRate}x)`}
              />
            </Select>
          </ButtonGroup>
        </div>
      </Wrapper>
    </Container>
  );
};

export default Controls;
